import { useEffect, useRef, useState } from "react";
import type { RiskPlusReport } from "../../entities/report/RiskPlusReport";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import type { ChartOptions } from "chart.js";
import { Radar } from 'react-chartjs-2';


ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);



export interface IExternalRiskRadarProps {
  reportData: RiskPlusReport
}

export default function ExternalRiskRadarChart({ reportData }: IExternalRiskRadarProps) {
  const chartRef = useRef<ChartJS<"radar"> | null>(null);
  const [chartImage, setChartImage] = useState<string | null>(null);

  // --- helpers ---
  const getRiskValue = (harmRank: string): number => {
    switch (harmRank.toLowerCase()) {
      case "critical":
        return 4;
      case "high":
        return 3;
      case "moderate":
        return 2;
      case "low":
        return 1;
      default:
        return 1;
    }
  };

  const aiImpact = reportData.deploymentProfile.aiSystemImpact;

  const externalDataValues = [
    getRiskValue(aiImpact.external.people.harmRank),
    getRiskValue(aiImpact.external.organisations.harmRank),
    getRiskValue(aiImpact.external.ecosystems.harmRank),
  ];

  const externalData = {
    labels: ["People", "Organisations", "Ecosystems"],
    datasets: [
      {
        label: "External Risk Impact",
        data: externalDataValues,
        backgroundColor: "rgba(255, 72, 72, 0.2)",
        borderColor: "rgba(255, 72, 72, 1)",
        borderWidth: 2,
        pointBackgroundColor: "rgba(255, 72, 72, 1)",
      },
    ],
  };

  const RENDER_DPR = 3;

  const options: ChartOptions<"radar"> = {
    responsive: false,
    maintainAspectRatio: false,
    animation: false,
    devicePixelRatio: 3,
    layout: {
      padding: 0,
    },
    scales: {
      r: {
        min: 0,
        max: 5,
        ticks: {
          stepSize: 1,
          display: false,
        },
        pointLabels: {
          padding: 4,
          font: {
            size: 12,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      const chart = chartRef.current;
      if (!chart) return;

      const canvas = chart.canvas;
      const { top, bottom, left, right } = chart.chartArea;

      const bufferLeft = 20;
      const bufferRight = 45;
      const cropX = Math.max(0, (left - bufferLeft) * RENDER_DPR);
      const cropY = Math.max(0, (top - bufferLeft) * RENDER_DPR);
      const cropW = Math.min(canvas.width, (right + bufferRight) * RENDER_DPR) - cropX;
      const cropH = Math.min(canvas.height, (bottom + bufferLeft) * RENDER_DPR) - cropY;

      const cropped = document.createElement("canvas");
      cropped.width = cropW;
      cropped.height = cropH;
      const ctx = cropped.getContext("2d");
      if (ctx) {
        ctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);
        setChartImage(cropped.toDataURL("image/png"));
      }
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="radar-chart-wrapper">
      {chartImage ? (
        <img
          src={chartImage}
          alt="External Risk Radar"
          className="w-full"
        />
      ) : (
        <Radar
          ref={chartRef}
          data={externalData}
          options={options}
          width={440}
          height={200}
        />
      )}
    </div>
  );

}
